import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface Farm {
  id: string
  name: string
  description: string
  location: {
    address: string
    latitude: number
    longitude: number
    city: string
    state: string
    country: string
  }
  size_hectares: number
  farm_type: 'organic' | 'conventional' | 'hydroponic' | 'greenhouse'
  certifications: string[]
  owner_id: string
  tenant_id: string
  is_active: boolean
  created_at: string
  updated_at: string
}

interface FarmsState {
  farms: Farm[]
  selectedFarm: Farm | null
  isLoading: boolean
  error: string | null
  pagination: {
    current_page: number
    total_pages: number
    total_items: number
    items_per_page: number
  }
}

const initialState: FarmsState = {
  farms: [],
  selectedFarm: null,
  isLoading: false,
  error: null,
  pagination: {
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 10,
  },
}

const farmsSlice = createSlice({
  name: 'farms',
  initialState,
  reducers: {
    setFarms: (state, action: PayloadAction<Farm[]>) => {
      state.farms = action.payload
    },
    addFarm: (state, action: PayloadAction<Farm>) => {
      state.farms.unshift(action.payload)
    },
    updateFarm: (state, action: PayloadAction<Farm>) => {
      const index = state.farms.findIndex(farm => farm.id === action.payload.id)
      if (index !== -1) {
        state.farms[index] = action.payload
      }
    },
    removeFarm: (state, action: PayloadAction<string>) => {
      state.farms = state.farms.filter(farm => farm.id !== action.payload)
    },
    setSelectedFarm: (state, action: PayloadAction<Farm | null>) => {
      state.selectedFarm = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setPagination: (state, action: PayloadAction<Partial<FarmsState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
  },
})

export const {
  setFarms,
  addFarm,
  updateFarm,
  removeFarm,
  setSelectedFarm,
  setLoading,
  setError,
  setPagination,
} = farmsSlice.actions

export default farmsSlice.reducer
